import merge from 'lodash/merge';

// ----------------------------------------------------------------------

type ApexOptions = any; // Using any to avoid SSR issues with apexcharts types

const useChart = (options?: ApexOptions) => {
  // Define theme tokens based on Tailwind/shadcn
  const themeTokens = {
    primary: 'hsl(var(--primary))',
    warning: 'hsl(var(--warning))',
    info: 'hsl(var(--info))',
    error: 'hsl(var(--destructive))',
    success: 'hsl(var(--success))',
    text: {
      primary: 'hsl(var(--foreground))',
      secondary: 'hsl(var(--muted-foreground))',
      disabled: 'hsl(var(--muted-foreground))'
    },
    background: {
      paper: 'hsl(var(--background))'
    },
    divider: 'hsl(var(--border))',
    grey: {
      500: 'hsl(var(--muted))'
    }
  };

  const LABEL_TOTAL = {
    show: true,
    label: 'Total',
    color: themeTokens.text.secondary,
    fontSize: '1rem', // text-base
    fontWeight: 500, // font-medium
    lineHeight: '1.5' // leading-normal
  };

  const LABEL_VALUE = {
    offsetY: 8,
    color: themeTokens.text.primary,
    fontSize: '1.875rem', // text-3xl
    fontWeight: 700, // font-bold
    lineHeight: '2.25rem' // leading-9
  };

  const baseOptions = {
    // TODO: Edit chart colors after the new design

    // colors: [
    //   'red',
    //   themeTokens.warning,
    //   themeTokens.info,
    //   themeTokens.error,
    //   themeTokens.success,
    //   themeTokens.warning,
    //   themeTokens.success,
    //   themeTokens.info,
    //   themeTokens.info
    // ],

    // Chart
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: themeTokens.text.disabled,
      fontFamily: 'var(--font-sans)' // Using shadcn font variable
    },

    // States
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.04
        }
      },
      active: {
        filter: {
          type: 'darken',
          value: 0.88
        }
      }
    },

    // Fill
    fill: {
      opacity: 1,
      gradient: {
        type: 'vertical',
        shadeIntensity: 0,
        opacityFrom: 0.4,
        opacityTo: 0,
        stops: [0, 100]
      }
    },

    // Datalabels
    dataLabels: {
      enabled: false
    },

    // Stroke
    stroke: {
      width: 1.5,
      curve: 'smooth',
      lineCap: 'round'
    },

    // Grid
    grid: {
      strokeDashArray: 3,
      borderColor: themeTokens.divider,
      xaxis: {
        lines: {
          show: false
        }
      }
    },

    // Xaxis
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false }
    },

    // Markers
    markers: {
      size: 0,
      strokeColors: themeTokens.background.paper
    },

    // Tooltip
    tooltip: {
      x: {
        show: true
      }
    },

    // Legend
    legend: {
      show: true,
      fontSize: '12px', // text-sm
      position: 'top',
      horizontalAlign: 'right',
      markers: {
        radius: 12
      },
      fontWeight: 500, // font-medium
      itemMargin: {
        horizontal: 8
      },
      labels: {
        colors: themeTokens.text.primary
      }
    },

    // plotOptions
    plotOptions: {
      // Bar
      bar: {
        borderRadius: 4,
        columnWidth: '28%',
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last'
      },

      // Pie + Donut
      pie: {
        donut: {
          labels: {
            show: true,
            value: LABEL_VALUE,
            total: LABEL_TOTAL
          }
        }
      },

      // Radialbar
      radialBar: {
        track: {
          strokeWidth: '100%',
          background: `${themeTokens.grey[500]}26` // Adding 26 for 15% opacity
        },
        dataLabels: {
          value: LABEL_VALUE,
          total: LABEL_TOTAL
        }
      },

      // Radar
      radar: {
        polygons: {
          fill: { colors: ['transparent'] },
          strokeColors: themeTokens.divider,
          connectorColors: themeTokens.divider
        }
      },

      // polarArea
      polarArea: {
        rings: {
          strokeColor: themeTokens.divider
        },
        spokes: {
          connectorColors: themeTokens.divider
        }
      }
    },

    // Responsive - using Tailwind breakpoints
    responsive: [
      {
        // sm - 640px
        breakpoint: 640,
        options: {
          plotOptions: { bar: { columnWidth: '40%' } }
        }
      },
      {
        // md - 768px
        breakpoint: 768,
        options: {
          plotOptions: { bar: { columnWidth: '32%' } }
        }
      }
    ]
  };

  return merge(baseOptions, options);
};

export default useChart;
